/**
 * TODO(deprecation): to be deprecated soon in favour of the same-origin BFF token
 * broker (KAN-2036 — `api/idp/[...path].ts` + the shared
 * `<DhruvantaAuth config={{ posture: 'bff' }}>` card). DO NOT build new work on this
 * module.
 *
 * WHY: every function here runs the IdP ceremony IN THE BROWSER and returns LIVE IdP
 * `accessToken` / `idToken` values into the JS heap. That breaks the platform's
 * token-free-browser invariant — an XSS on any retail portal could exfiltrate a
 * whole Dhruvanta identity, not just a retail session. Under KAN-2036 the identical
 * ceremony runs SERVER-SIDE and the tokens are sealed in an httpOnly AES-256-GCM
 * cookie the browser cannot read.
 *
 * It is retained ONLY as the no-code-change rollback reached by
 * `VITE_DHRUVANTA_AUTH_MODE=legacy`. Once the BFF path is proven live this gets a
 * full `@deprecated` marker and a removal ticket.
 *
 * ---------------------------------------------------------------------------
 * "Sign in with Dhruvanta" — API-first IdP client (KAN-668 / KAN-747 / KAN-1500).
 *
 * KAN-747 — this REPLACES the old browser-redirect OIDC flow. The Dhruvanta IdP
 * (identity.dhruvantasystems.net) is API-FIRST: `GET /oauth2/authorize` answers 401 for a cold
 * user (there is NO hosted login page to redirect to), and `/oauth2/token` speaks JSON+camelCase
 * (not form-encoded snake_case). The old redirect flow could therefore never deliver a usable
 * token. This module drives the IdP's API-first password login exactly as PrintAnywhere's proven
 * `@dhruvanta/auth-client.loginWithPassword` does, with NO redirect:
 *
 *   1. discover the OIDC endpoints (`/.well-known/openid-configuration`),
 *   2. POST credentials + a fresh PKCE/S256 transaction (JSON, camelCase) to the authorize
 *      endpoint — the IdP returns a `redirectUri` carrying an authorization `code`,
 *   3. POST that `code` + the PKCE `codeVerifier` (JSON, camelCase) to the token endpoint — the IdP
 *      returns BOTH an app-scoped user `accessToken` (aud=user.retail-web.dhruvantasystems.net,
 *      realm=user) AND an `idToken` (the verified email).
 *
 * The caller POSTs BOTH tokens to the retail bridge (`POST /api/v2/auth/oidc/dhruvanta`), which
 * verifies the access token for entitlement and reads the email from the id_token.
 *
 * We call the IdP REST surface directly (rather than depend on the private GH-Packages
 * `@dhruvanta/auth-client`, which would need an NPM_TOKEN in the Vercel build) — the contract is
 * small and documented, and this keeps the flag-gated-off feature install-free.
 *
 * FLAG-GATED + env-driven: enabled only when VITE_DHRUVANTA_SSO_ENABLED === 'true' AND the
 * issuer/clientId are configured. With the flag off this code is inert — zero impact on existing
 * password / OTP auth.
 */

export interface DhruvantaSsoConfig {
  issuer: string
  clientId: string
  /** Registered redirect URI for this app's IdP client; part of the PKCE transaction. */
  redirectUri: string
  scope: string
}

/** The two IdP tokens the retail bridge needs (entitlement + verified email). */
export interface DhruvantaTokens {
  accessToken: string
  idToken: string
}

/** Signals the IdP demanded a second factor — surfaced so the UI can route the user to OTP. */
export class DhruvantaMfaRequiredError extends Error {
  constructor() {
    super('Two-step verification is required to sign in with Dhruvanta.')
    this.name = 'DhruvantaMfaRequiredError'
  }
}

/** Whether the feature flag is on. The button/flow no-op when this is false. */
export function isDhruvantaSsoEnabled(): boolean {
  return import.meta.env.VITE_DHRUVANTA_SSO_ENABLED === 'true'
}

/**
 * Resolve the IdP issuer. When VITE_DHRUVANTA_SSO_SAME_ORIGIN=true the issuer
 * is the same-origin /idp base (KAN-1500 — mobile cookie fix: all IdP calls go
 * through a same-origin Vercel function that forwards to identity.*.net and
 * rewrites Set-Cookie to first-party). When the flag is off the explicit
 * VITE_DHRUVANTA_ISSUER env var is used (direct .net call; desktop-only safe).
 * Same pattern as RTP src/lib/auth/idpConfig.ts resolveIdpIssuer().
 */
function resolveIssuer(): string | undefined {
  if (import.meta.env.VITE_DHRUVANTA_SSO_SAME_ORIGIN === 'true') {
    return `${window.location.origin}/idp`
  }
  return import.meta.env.VITE_DHRUVANTA_ISSUER
}

/** Resolve the env-driven config, or null when issuer/clientId are not set. */
export function getDhruvantaSsoConfig(): DhruvantaSsoConfig | null {
  const issuer = resolveIssuer()
  const clientId = import.meta.env.VITE_DHRUVANTA_CLIENT_ID
  if (!issuer || !clientId) return null
  const redirectUri =
    import.meta.env.VITE_DHRUVANTA_REDIRECT_URI ||
    `${window.location.origin}/auth/dhruvanta/callback`
  const scope = import.meta.env.VITE_DHRUVANTA_SCOPE || 'openid email profile'
  return { issuer: issuer.replace(/\/$/, ''), clientId, redirectUri, scope }
}

interface OidcDiscovery {
  authorization_endpoint: string
  token_endpoint: string
}

async function discover(issuer: string): Promise<OidcDiscovery> {
  const res = await fetch(`${issuer}/.well-known/openid-configuration`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Dhruvanta sign-in is unavailable (discovery ${res.status})`)
  const doc = (await res.json()) as Partial<OidcDiscovery>
  if (!doc.authorization_endpoint || !doc.token_endpoint) {
    throw new Error('Dhruvanta sign-in is misconfigured (discovery document is missing endpoints)')
  }
  return {
    authorization_endpoint: doc.authorization_endpoint,
    token_endpoint: doc.token_endpoint,
  }
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  let str = ''
  for (const byte of new Uint8Array(bytes)) str += String.fromCharCode(byte)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(byteLength = 48): string {
  const arr = new Uint8Array(byteLength)
  window.crypto.getRandomValues(arr)
  return base64UrlEncode(arr.buffer)
}

async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(digest)
}

/** The IdP authorize response (camelCase) — either a redirectUri with the code, or an MFA challenge. */
interface AuthorizeApiResponse {
  redirectUri?: string
  mfaRequired?: boolean
  mfaChallenge?: string
}

/** The IdP token response (camelCase, with snake_case fallbacks for a contract-compliant mock). */
interface TokenResponse {
  accessToken?: string
  idToken?: string
  access_token?: string
  id_token?: string
}

/** POST JSON to the IdP, credentialed (so it can set/read its SameSite SSO cookie). Throws on !ok. */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    // The IdP returns RFC-7807 problem+json on a credential failure; a 401 is bad credentials.
    let detail = `HTTP ${res.status}`
    try {
      const problem = (await res.json()) as { detail?: string; message?: string }
      detail = problem.detail || problem.message || detail
    } catch {
      // non-JSON body; keep the status detail.
    }
    if (res.status === 401) {
      throw new Error('Incorrect email or password. Please try again.')
    }
    throw new Error(`Dhruvanta sign-in failed: ${detail}`)
  }
  return (await res.json()) as T
}

/** Extract the authorization `code` from the IdP's returned redirectUri. */
function codeFromRedirectUri(redirectUri: string | undefined): string {
  if (!redirectUri) {
    throw new Error('Dhruvanta sign-in did not return an authorization code.')
  }
  const params = new URL(redirectUri).searchParams
  const error = params.get('error')
  if (error) {
    throw new Error(`Dhruvanta sign-in failed: ${params.get('error_description') || error}`)
  }
  const code = params.get('code')
  if (!code) throw new Error('Dhruvanta sign-in did not return an authorization code.')
  return code
}

/**
 * API-first PASSWORD login against the Dhruvanta IdP (no redirect). On success returns BOTH the
 * app-scoped user access token AND the id_token for the retail bridge. Throws on bad credentials,
 * and throws {@link DhruvantaMfaRequiredError} when the IdP demands a second factor (so the UI can
 * point the user at the one-time-code path).
 */
export async function loginWithDhruvantaPassword(
  email: string,
  password: string,
): Promise<DhruvantaTokens> {
  const config = getDhruvantaSsoConfig()
  if (!config) throw new Error('Dhruvanta sign-in is not configured')
  const { authorization_endpoint, token_endpoint } = await discover(config.issuer)

  // Fresh PKCE(S256) + state transaction (kept in-memory; no redirect round-trips it).
  const state = randomString(24)
  const nonce = randomString(24)
  const codeVerifier = randomString(48)
  const codeChallenge = await pkceChallenge(codeVerifier)

  // Step 1: POST credentials + the transaction (JSON, camelCase) to the authorize endpoint.
  const authorize = await postJson<AuthorizeApiResponse>(authorization_endpoint, {
    responseType: 'code',
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    scope: config.scope,
    state,
    nonce,
    codeChallenge,
    codeChallengeMethod: 'S256',
    email,
    password,
  })
  if (authorize.mfaRequired) {
    // No inline second-factor step: the IdP accepts an email OTP as a verified factor, so we point
    // the user at the retail-native one-time-code sign-in rather than failing silently.
    throw new DhruvantaMfaRequiredError()
  }
  const code = codeFromRedirectUri(authorize.redirectUri)

  // Step 2: exchange the code + PKCE verifier (JSON, camelCase) at the token endpoint.
  const tokens = await postJson<TokenResponse>(token_endpoint, {
    grantType: 'authorization_code',
    code,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    codeVerifier,
  })
  const accessToken = tokens.accessToken || tokens.access_token
  const idToken = tokens.idToken || tokens.id_token
  if (!accessToken) throw new Error('Dhruvanta sign-in did not return an access token.')
  if (!idToken) throw new Error('Dhruvanta sign-in did not return an id token.')
  return { accessToken, idToken }
}
